import {
  Heading,
  SearchImage,
  ShowMore,
} from '@rabbit/elements/shared-components';
import ROUTE_NAME from '../../../utils/url-constants';
import { DTVendable } from '@rabbit/data/types';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

const INITIAL_VISIBLE = 5;
const LOAD_MORE_STEP = 5;

export interface SliderProps {
  title: string;
  actionButtonText: string;
  key?: string;
  data?: DTVendable[];
  onClick: (value: string) => void;
  onItemSelect?: (id: string, data: any) => void;
  /** When true, "Show More" reveals more items in the slider. When false, "Show More" calls onClick(title). Default false. */
  loadMoreInPlace?: boolean;
}

export function SearchSlider({
  title,
  actionButtonText,
  data,
  onClick,
  onItemSelect,
  loadMoreInPlace = false,
}: SliderProps) {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    if (data?.length && loadMoreInPlace) {
      setVisibleCount(Math.min(INITIAL_VISIBLE, data.length));
    }
  }, [data?.length, loadMoreInPlace]);

  const visibleData =
    loadMoreInPlace && data
      ? data.slice(0, visibleCount)
      : data?.slice(0, INITIAL_VISIBLE) ?? [];
  const showMoreCard =
    loadMoreInPlace
      ? data && visibleCount < data.length
      : data && data.length > INITIAL_VISIBLE;

  const handleShowMoreClick = (value: string) => {
    if (loadMoreInPlace && data) {
      setVisibleCount((prev) =>
        Math.min(prev + LOAD_MORE_STEP, data.length)
      );
    } else {
      onClick(value);
    }
  };

  const handleShowAllClick = () => {
    if (loadMoreInPlace && data) {
      setVisibleCount(data.length);
    } else {
      onClick(title);
    }
  };

  return data ? (
    <div className="w-full max-w-screen-xl">
      <div className="mt-[23px] flex justify-between capitalize">
        <Heading kind="h3" children={title} />
        {data.length > 10 ? (
          <p
            className="text-primary-800 cursor-pointer text-base"
            onClick={handleShowAllClick}
          >
            {actionButtonText}
          </p>
        ) : null}
      </div>
      <div className="flex gap-1 overflow-auto pb-3">
        {visibleData.map((item: any, index: number) => (
          <div
            key={index}
            className="font-nunito mr-[19px] mt-3 flex-col items-center justify-center gap-2 rounded-lg"
          >
            <SearchImage
              imageUrl={item.img[0]}
              size="sm"
              subtitle={item.brand}
              title={item.title}
              onClick={
                !onItemSelect
                  ? () =>
                      navigate(`${ROUTE_NAME.PRODUCTS_PREVIEW}/${item.docid}`)
                  : () => onItemSelect(item.docid, item)
              }
            />
          </div>
        ))}
        {showMoreCard ? (
          <div className="mr-5 mt-3 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg">
            <ShowMore
              actionText="Show More"
              onClick={handleShowMoreClick}
              category={title}
            />
          </div>
        ) : null}
      </div>
    </div>
  ) : null;
}

export default SearchSlider;
